import React from 'react';

// Native select / textarea styled to match Input.tsx.

const base =
  'w-full px-3 py-2 border rounded-md min-h-[44px] lg:min-h-0 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-150 text-ink-900 bg-white';

function Label({ label, required }: { label?: string; required?: boolean }) {
  if (!label) return null;
  return (
    <label className="block text-xs font-bold uppercase tracking-[0.06em] text-ink-600 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}

export function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-red-600">
      <i className="fas fa-circle-exclamation mr-1" />
      {error}
    </p>
  );
}

export function NativeSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  helperText,
  required,
  disabled,
  className = '',
}: {
  label?: string;
  value: string | number;
  onChange: (value: string) => void;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label label={label} required={required} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={label ? undefined : placeholder}
        aria-invalid={error ? true : undefined}
        className={`${base} ${error ? 'border-red-500 focus:ring-red-500' : 'border-ink-200 focus:ring-brand-500'} ${
          disabled ? 'bg-ink-50 cursor-not-allowed' : ''
        }`}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <FieldError error={error} />
      {!error && helperText && <p className="mt-1 text-xs text-ink-500">{helperText}</p>}
    </div>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  rows = 2,
  placeholder,
  error,
  maxLength,
  required,
  disabled,
  className = '',
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  error?: string;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label label={label} required={required} />
      <textarea
        rows={rows}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`${base} ${error ? 'border-red-500 focus:ring-red-500' : 'border-ink-200 focus:ring-brand-500'} ${
          disabled ? 'bg-ink-50 cursor-not-allowed' : ''
        }`}
      />
      <div className="flex justify-between gap-3">
        <FieldError error={error} />
        {/* Character counter appears once the limit is close */}
        {maxLength !== undefined && value.length >= maxLength * 0.8 && (
          <p className={`mt-1 ml-auto text-[11px] ${value.length >= maxLength ? 'text-red-600' : 'text-ink-400'}`}>
            {value.length}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
}

// <input type="month"> with the same label / error treatment as Input.
export function MonthInput({
  label,
  value,
  onChange,
  error,
  required,
  max,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  max?: string;
}) {
  return (
    <div>
      <Label label={label} required={required} />
      <input
        type="month"
        value={value}
        max={max}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`${base} ${error ? 'border-red-500 focus:ring-red-500' : 'border-ink-200 focus:ring-brand-500'}`}
      />
      <FieldError error={error} />
    </div>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  hint,
}: {
  label: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
      />
      <span>
        <span className="text-sm text-ink-800">{label}</span>
        {hint && <span className="block text-xs text-ink-500">{hint}</span>}
      </span>
    </label>
  );
}
